import { lazy, Suspense, useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import RevealOnScroll from "@/components/RevealOnScroll";

// Lazy: SVG-Backdrop nur auf Desktop und nach Mount laden — hält das
// initial Mobile-Bundle frei vom inline SVG und framer-motion.
const HeroBackdrop = lazy(() => import("./HeroBackdrop"));
const HeroVideoLayer = lazy(() => import("./HeroVideoLayer"));

const HeroSection = () => {
  const { t } = useLanguage();
  const lines = t.hero.headline.split("\n");
  const [showBackdrop, setShowBackdrop] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
    if (!isDesktop) return;
    const ric =
      (window as unknown as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback ??
      ((cb: () => void) => window.setTimeout(cb, 200));
    ric(() => setShowBackdrop(true));
  }, []);

  return (
    <section id="hero" className="relative min-h-[90vh] flex items-center pt-16 bg-primary text-primary-foreground overflow-hidden">
      {showBackdrop && (
        <Suspense fallback={null}>
          <HeroVideoLayer />
          <HeroBackdrop />
        </Suspense>
      )}

      <div className="container mx-auto px-4 lg:px-8 py-24 lg:py-32 relative z-10">
        <RevealOnScroll className="max-w-3xl">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.08] mb-6 uppercase">
            {lines.map((line, i) => (
              <span key={i}>
                {line}
                {i < lines.length - 1 && <br />}
              </span>
            ))}
          </h1>
          <p className="text-lg sm:text-xl text-primary-foreground/70 max-w-xl mb-4 leading-relaxed">
            {t.hero.subline}
          </p>
          <p className="text-base text-primary-foreground/50 max-w-xl mb-10 leading-relaxed">
            {t.hero.tagline}
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
            <Button
              size="lg"
              className="rounded-full px-8 text-base gap-2 bg-primary-foreground text-primary hover:bg-primary-foreground/90"
              asChild
            >
              <a href="#lead">
                {t.hero.ctaPrimary} <ArrowRight size={18} />
              </a>
            </Button>
            <Button
              size="lg"
              className="rounded-full px-8 text-base gap-2 bg-primary-foreground/15 text-primary-foreground border border-primary-foreground/40 hover:bg-primary-foreground/25"
              asChild
            >
              <a href="/investitionsrechner">
                {t.hero.ctaSecondary} <ArrowRight size={18} />
              </a>
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="rounded-full px-8 text-base bg-transparent border-primary-foreground/60 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground hover:border-primary-foreground/80"
              asChild
            >
              <a href="#prozess">{t.hero.ctaTertiary}</a>
            </Button>
          </div>
        </RevealOnScroll>
      </div>
    </section>
  );
};

export default HeroSection;
